import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

export default function BusinessModelUnitEconomics() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-6">
      <SlideHeader eyebrow="Economics">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          Expert ASO at the price of a tool.
        </h1>
        <p className="mt-3 text-xl font-medium tracking-tight text-slate-700 md:mt-4 md:text-3xl">
          The deliverable is expert work — continuous, every-app, every-market
          ASO — at self-serve SaaS pricing: €49–€399/month, enterprise above.
        </p>
      </SlideHeader>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-6">
        <Card label="Pricing tiers (live)">
          <table className="mt-3 w-full border-collapse text-xs md:text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wide text-slate-500 md:text-xs">
                <th className="pb-1.5 text-left">Tier</th>
                <th className="pb-1.5 text-right">Price/mo</th>
              </tr>
            </thead>
            <tbody className="text-slate-800">
              <PricingRow tier="Free" price="€0" />
              <PricingRow tier="Basic" price="€49" />
              <PricingRow tier="Pro" price="€149" />
              <PricingRow tier="Elite" price="€399" />
              <PricingRow tier="Enterprise" price="Flexible" />
            </tbody>
          </table>
          <p className="mt-3 text-xs text-slate-500 md:text-sm">
            Annual billing: 17% off. Self-serve signup; free trial; Stripe
            billing; 30% affiliate revenue share.
          </p>
        </Card>

        <Card label="Unit economics (model)">
          <dl className="mt-3 space-y-2 text-xs md:text-sm">
            <UEMetric label="Gross margin" value="≥ 85%" sub="~98% today, measured" />
            <UEMetric label="LTV" value="€3,072" sub="€128 × 24 mo" />
            <UEMetric
              label="Self-serve CAC"
              value="€120 → ~€167"
              sub="today → m24 w/ channel saturation"
            />
            <UEMetric label="LTV:CAC" value="~18:1" sub="floor ≥ 5:1" />
            <UEMetric label="Payback" value="~1.3 mo" sub="target ≤ 8 mo" />
          </dl>
        </Card>

        <Card label="Month 24 (base plan)">
          <p className="mt-3 text-xl font-bold tracking-tight text-blue-700 md:text-2xl">
            €2.8M ARR
          </p>
          <p className="text-xs text-slate-500 md:text-sm">
            1,611 paying accounts · €235K MRR
          </p>
          <table className="mt-3 w-full border-collapse text-xs md:text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wide text-slate-500 md:text-xs">
                <th className="pb-1.5 text-left">Segment</th>
                <th className="pb-1.5 text-right">Accts</th>
                <th className="pb-1.5 text-right">Mo. rev</th>
              </tr>
            </thead>
            <tbody className="text-slate-800">
              <MixRow tier="Self-serve (€128 ARPU)" accts="1,585" rev="€203K" />
              <MixRow tier="Enterprise (€1,250)" accts="26" rev="€32K" />
            </tbody>
          </table>
          <p className="mt-3 text-xs text-slate-500 md:text-sm">
            Enterprise ≈ 14% of MRR on ~2% of accounts. From the operating
            model, growing 2.7× YoY at that point.
          </p>
        </Card>
      </div>

    </div>
  );
}

function Card({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200 md:p-5">
      <p className="text-sm font-semibold uppercase tracking-[0.15em] text-blue-700 md:text-base">
        {label}
      </p>
      {children}
    </div>
  );
}

function PricingRow({ tier, price }: { tier: string; price: string }) {
  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      <td className="py-1.5">{tier}</td>
      <td className="py-1.5 text-right tabular-nums">{price}</td>
    </tr>
  );
}

function MixRow({
  tier,
  accts,
  rev,
}: {
  tier: string;
  accts: string;
  rev: string;
}) {
  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      <td className="py-1.5">{tier}</td>
      <td className="py-1.5 text-right tabular-nums">{accts}</td>
      <td className="py-1.5 text-right tabular-nums">{rev}</td>
    </tr>
  );
}

function UEMetric({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-slate-200 pb-2 last:border-b-0">
      <div>
        <dt className="font-medium text-slate-700">{label}</dt>
        <dd className="text-[10px] text-slate-500 md:text-xs">{sub}</dd>
      </div>
      <span className="tabular-nums font-semibold text-slate-900">
        {value}
      </span>
    </div>
  );
}
